import * as React from 'react';

import { observer } from 'mobx-react';
import styled from 'styled-components';

import { ArrowIcon } from '@estee/elc-icons';

import { ICheckableItem } from '~domain/entities/Config';
import { ckGroupItemWrapperClasses, classes, fieldLabelClasses } from '~utils/Classes';

import { ICheckboxGroupProps } from './CheckboxGroup';
import { FormItemAdapter } from './FormItemAdapter';

interface ICheckboxGroupViewProps extends Partial<ICheckboxGroupProps> {
    onChange(isChecked: boolean, fieldName: string): void;
}

interface IAccordionCheckboxGroup extends ICheckboxGroupProps {
    expanded?: boolean;
}

const SelectAllCheckboxWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const CheckboxItemsWrapper = styled.div`
    padding-left: 30px;
    max-height: 0px;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
`;

const SelectAllLabel = styled.label`
    cursor: pointer;
`;

export const TEST_ID_CHECKBOX_GROUP = 'checkbox-group';
const itemRowsWrapperClassName = classes('iam-checkboxGroup-rows-wrapper');
const checkboxGroupSelectAllClassName = classes('iam-checkbox-group-select-all');
const checkboxGroupToggleClassName = classes('iam-checkbox-group-toggle');
const checkboxGroupItemsClassName = classes('iam-checkbox-group-items');

const getGroupFieldNames = (options: ICheckableItem[]) =>
    options.reduce((acc: string[], item: ICheckableItem) => {
        acc.push(item.name);

        return acc;
    }, []);

const getCheckboxGroupRows = ({ items, enabled, onChange, formStore }: ICheckboxGroupViewProps) => {
    return items?.map(({ name, labelKey }) => {
        return (
            <div className={ckGroupItemWrapperClasses(name)} key={labelKey}>
                <FormItemAdapter
                    {...{
                        name,
                        labelKey,
                        enabled,
                        onChange,
                        inputType: 'checkbox',
                        formStore
                    }}
                />
            </div>
        );
    });
};

const getCheckboxGroup = (props: IAccordionCheckboxGroup) => {
    const { items, selectAll, enabled, formStore, expanded = false } = props;
    const [selectAllFlag, setSelectAllFlag] = React.useState(false);
    const [expandFlag, setExpandFlag] = React.useState(expanded);

    if (!selectAll) {
        return;
    }

    const { name: selectAllCheckboxName } = selectAll ?? {};
    const hasError = [...new Set(items?.map((item) => item.name))].some((key) =>
        formStore?.getError(key)
    );

    const checkboxNames = React.useMemo(() => getGroupFieldNames(items), [items]);

    const onChange = React.useCallback((isChecked: boolean, fieldName: string) => {
        if (!selectAllCheckboxName) {
            return;
        }
        if (selectAllCheckboxName === fieldName) {
            // TODO: APACRIGEL-459 need to reuse the logic from Checkbox
            // Set item check status once select all is changed
            checkboxNames.map((name) => {
                formStore.setFieldValue(name, isChecked ? 1 : 0);
                formStore.blurField(name);
            });
            setSelectAllFlag(isChecked);
        } else {
            formStore.setFieldValue(
                selectAllCheckboxName,
                checkboxNames.every((name: string) => !!formStore.values[name])
            );
            formStore.blurField(selectAllCheckboxName);
        }
    }, []);

    React.useEffect(() => {
        if (hasError && !expandFlag && !selectAllFlag) {
            setExpandFlag(true);
        }
    }, [hasError, expandFlag, selectAllFlag]);

    return (
        <>
            <SelectAllCheckboxWrapper className={checkboxGroupSelectAllClassName}>
                <FormItemAdapter
                    {...{
                        ...selectAll,
                        enabled,
                        onChange,
                        inputType: 'checkbox',
                        formStore
                    }}
                />

                <SelectAllLabel
                    htmlFor={selectAllCheckboxName}
                    onClick={() => setExpandFlag(!expandFlag)}
                >
                    <ArrowIcon isOpen={expandFlag} />
                </SelectAllLabel>
            </SelectAllCheckboxWrapper>
            <input
                id={selectAllCheckboxName}
                checked={expandFlag}
                className={checkboxGroupToggleClassName}
                type="checkbox"
            />
            <CheckboxItemsWrapper className={checkboxGroupItemsClassName}>
                {getCheckboxGroupRows({
                    items,
                    enabled,
                    onChange,
                    formStore
                })}
            </CheckboxItemsWrapper>
        </>
    );
};

export const AccordionCheckboxGroup = observer((props: IAccordionCheckboxGroup) => {
    const { inputLabel, name: groupName } = props;

    return (
        <>
            {inputLabel && (
                <div
                    className={fieldLabelClasses(groupName)}
                    data-test-id={TEST_ID_CHECKBOX_GROUP}
                    dangerouslySetInnerHTML={{ __html: inputLabel }}
                />
            )}
            <div className={itemRowsWrapperClassName}>{getCheckboxGroup({ ...props })}</div>
        </>
    );
});
